import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Chip,
  Button,
  ButtonGroup,
  useTheme,
} from '@mui/material';
import {
  AccessTimeOutlined,
  ArrowDropDown,
  ArrowDropUp,
  HowToRegOutlined,
  InfoOutlined,
  InsightsOutlined,
  LanguageOutlined,
  PeopleAltOutlined,
  PersonAddAlt1Outlined,
  ThumbUpOffAltOutlined,
  TrendingFlat,
  VisibilityOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import { ResponsiveLine, Serie } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import Header from '../../components/Header';
import { tokens } from '../../theme';
import { FilterContext } from '../../contexts/FilterContext';
import { api } from '../../helpers/apiUtils';
import { determineAggregation } from '../../helpers/chartUtils';
import PortalComparisonView from './PortalComparisonView';
import {
  AggregationMode,
  CompareRange,
  GROUP_ORDER,
  LeadsCompareResponse,
  METRIC_LABELS,
  METRIC_TOOLTIPS,
  MetricKey,
  Overview,
  OverviewCompareResponse,
  Portal,
  QUICK_RANGES,
  TimeseriesRow,
  aggregationToBucket,
  calcDelta,
  fmtDuration,
  fmtInt,
  fmtPercent,
  formatAggregationTick,
  toSqlDate,
} from './shared';

// ─── Types (local to single-portal view) ──────────────────────────────────

interface SourceRow {
  source: string;
  medium: string;
  sessions: number;
  users: number;
}
interface CountryRow {
  country: string;
  sessions: number;
  users: number;
}
interface DeviceRow {
  deviceCategory: string;
  sessions: number;
  users: number;
}
interface PageRow {
  pagePath: string;
  pageViews: number;
  users: number;
}

// ─── KPI card ──────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: string;
  previousValue?: string;
  deltaPct?: number | null; // null = no comparison available (prev=0)
  higherIsBetter?: boolean; // default true
  sub?: string;
  icon?: React.ReactNode;
  iconColor?: string;
  iconBg?: string;
  tooltip?: string; // hover help text shown next to the label
}

const KpiCard: React.FC<KpiCardProps> = ({
  label,
  value,
  previousValue,
  deltaPct,
  higherIsBetter = true,
  sub,
  icon,
  iconColor,
  iconBg,
  tooltip,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const hasDelta = deltaPct !== undefined && deltaPct !== null;
  const isUp = hasDelta && (deltaPct as number) > 0;
  const isDown = hasDelta && (deltaPct as number) < 0;
  const isFlat = hasDelta && (deltaPct as number) === 0;

  let deltaColor = colors.grey[100];
  if (hasDelta) {
    const good = isUp ? higherIsBetter : isDown ? !higherIsBetter : true;
    if (!isFlat) {
      deltaColor = good ? colors.greenAccent[400] : colors.redAccent[400];
    }
  }

  return (
    <Card
      sx={{
        backgroundColor: colors.primary[400],
        height: '100%',
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box display="flex" alignItems="center" gap={0.5} sx={{ pt: 0.25 }}>
            <Typography variant="subtitle2" color={colors.grey[300]} sx={{ lineHeight: 1.3 }}>
              {label}
            </Typography>
            {tooltip && (
              <Tooltip
                title={tooltip}
                arrow
                placement="top"
                enterDelay={300}
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '0.75rem',
                      lineHeight: 1.45,
                      maxWidth: 280,
                      backgroundColor: colors.primary[500],
                      color: colors.grey[100],
                      px: 1.25,
                      py: 1,
                    },
                  },
                  arrow: { sx: { color: colors.primary[500] } },
                }}
              >
                <InfoOutlined
                  sx={{
                    fontSize: 14,
                    color: colors.grey[400],
                    cursor: 'help',
                    '&:hover': { color: colors.grey[200] },
                  }}
                />
              </Tooltip>
            )}
          </Box>
          {icon && (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: iconBg,
                color: iconColor,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
        <Typography
          variant="h3"
          color={colors.grey[100]}
          sx={{ mt: 1.25, fontWeight: 'bold', letterSpacing: '-0.5px' }}
        >
          {value}
        </Typography>
        {hasDelta && (
          <Box display="flex" alignItems="center" mt={1} gap={0.75}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: `${deltaColor}22`,
                color: deltaColor,
                borderRadius: 10,
                px: 0.875,
                py: 0.125,
                fontSize: '0.72rem',
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              {isUp && <ArrowDropUp sx={{ color: deltaColor, fontSize: 16, ml: -0.5 }} />}
              {isDown && <ArrowDropDown sx={{ color: deltaColor, fontSize: 16, ml: -0.5 }} />}
              {isFlat && <TrendingFlat sx={{ color: deltaColor, fontSize: 12, mr: 0.25 }} />}
              {(deltaPct as number) > 0 ? '+' : ''}
              {(deltaPct as number).toFixed(1)}%
            </Box>
            {previousValue && (
              <Typography variant="caption" sx={{ color: colors.grey[300], fontSize: '0.72rem' }}>
                vs. {previousValue}
              </Typography>
            )}
          </Box>
        )}
        {sub && !hasDelta && (
          <Typography variant="caption" color={colors.grey[300]} sx={{ mt: 1, display: 'block' }}>
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Main component ────────────────────────────────────────────────────────

const Ga4TrafficPage: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState, setFilterState } = useContext(FilterContext)!;
  const location = useLocation();
  const navigate = useNavigate();

  // KPI card color groups: volume (blue), quality (green), conversion (amber).
  // Pre-compute once per render so the 8 card call sites stay terse.
  const kpiAccents = useMemo(
    () => ({
      volume: {
        iconColor: colors.blueAccent[400],
        iconBg: `${colors.blueAccent[700]}2E`, // ~18% alpha
      },
      quality: {
        iconColor: colors.greenAccent[400],
        iconBg: `${colors.greenAccent[700]}2E`,
      },
      conversion: {
        iconColor: '#f59e0b',
        iconBg: 'rgba(245, 158, 11, 0.15)',
      },
    }),
    [colors],
  );

  // Shared sx for top-level Paper containers — subtle shadow + rounded corners
  // for visual consistency with the polished KPI cards.
  const paperPolish = useMemo(
    () => ({
      borderRadius: 2,
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    }),
    [],
  );

  const [portals, setPortals] = useState<Portal[]>([]);
  const [groupLabels, setGroupLabels] = useState<Record<string, string>>({});

  // Initial state from URL (only on mount)
  const initialState = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const mode: 'single' | 'compare' = params.get('mode') === 'compare' ? 'compare' : 'single';
    const portal = params.get('portal') || 'finanztrends';
    const comparePortalsParam = params.get('portals');
    const comparePortals = comparePortalsParam
      ? comparePortalsParam.split(',').filter(Boolean)
      : [portal];
    return { mode, portal, comparePortals };
  }, []); // only on mount

  const [mode, setMode] = useState<'single' | 'compare'>(initialState.mode);
  const [selectedPortal, setSelectedPortal] = useState<string>(initialState.portal);
  const [comparePortals, setComparePortals] = useState<string[]>(initialState.comparePortals);
  const [activeMetric, setActiveMetric] = useState<MetricKey>('totalUsers');
  const [aggregationMode, setAggregationMode] = useState<AggregationMode>('auto');

  const [overview, setOverview] = useState<Overview | null>(null);
  const [previousOverview, setPreviousOverview] = useState<Overview | null>(null);
  const [previousRange, setPreviousRange] = useState<CompareRange | null>(null);
  const [timeseries, setTimeseries] = useState<TimeseriesRow[]>([]);
  const [sources, setSources] = useState<SourceRow[]>([]);
  const [countries, setCountries] = useState<CountryRow[]>([]);
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [pages, setPages] = useState<PageRow[]>([]);
  const [organicLeadsTotal, setOrganicLeadsTotal] = useState<number | null>(null);
  const [previousOrganicLeads, setPreviousOrganicLeads] = useState<number | null>(null);
  const [organicLeadsTimeseries, setOrganicLeadsTimeseries] = useState<
    { date: string; leads: number }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Separate error state for the leads endpoint so a leads failure doesn't
  // take down the whole page (critical-review finding #2): the traffic KPI
  // cards + chart + breakdowns still render, and the lead section shows a
  // dedicated warning. If leads is not expected for this portal (hasLeads=
  // false), this stays null.
  const [leadsError, setLeadsError] = useState<string | null>(null);

  const startStr = useMemo(
    () => (filterState.startDate ? toSqlDate(filterState.startDate) : ''),
    [filterState.startDate],
  );
  const endStr = useMemo(
    () => (filterState.endDate ? toSqlDate(filterState.endDate) : ''),
    [filterState.endDate],
  );

  // Resolve "auto" aggregation to a concrete bucket size based on date range
  const effectiveAggregation = useMemo<'daily' | 'weekly' | 'monthly'>(() => {
    if (aggregationMode !== 'auto') return aggregationMode;
    if (!startStr || !endStr) return 'daily';
    return determineAggregation(new Date(startStr), new Date(endStr));
  }, [aggregationMode, startStr, endStr]);

  // Sync portal / mode / comparePortals to URL (preserve existing params)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let changed = false;

    if (mode === 'compare') {
      if (params.get('mode') !== 'compare') {
        params.set('mode', 'compare');
        changed = true;
      }
      const csv = comparePortals.join(',');
      if (params.get('portals') !== csv) {
        if (csv) params.set('portals', csv);
        else params.delete('portals');
        changed = true;
      }
      if (params.has('portal')) {
        params.delete('portal');
        changed = true;
      }
    } else {
      if (params.get('mode') === 'compare') {
        params.delete('mode');
        changed = true;
      }
      if (params.has('portals')) {
        params.delete('portals');
        changed = true;
      }
      if (params.get('portal') !== selectedPortal) {
        params.set('portal', selectedPortal);
        changed = true;
      }
    }

    if (changed) {
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [mode, selectedPortal, comparePortals, location.search, navigate]);

  // Load portals once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/ga4/portals');
        if (cancelled) return;
        if (res.data?.success) {
          setPortals(res.data.data.portals);
          setGroupLabels(res.data.data.groupLabels);
        }
      } catch (err: any) {
        console.error('Failed to load portals:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch single-portal data whenever portal or date range changes.
  // Skipped in compare mode (PortalComparisonView handles its own fetching).
  useEffect(() => {
    if (mode !== 'single') return;
    if (!selectedPortal || !startStr || !endStr) return;
    // Wait for the portal registry to load so we can check `hasLeads`
    // before deciding whether to fire the leads request.
    if (portals.length === 0) return;

    const portalForFetch = portals.find((p) => p.key === selectedPortal);

    let cancelled = false;

    // Critical-review finding #1: clear ALL previously-rendered state when
    // the fetch key changes. Without this, a failed fetch would leave the
    // previous portal's data visible under an error banner — a data
    // misattribution bug. Resetting up-front means the page briefly shows
    // empty cards + a spinner, then populates or shows an error.
    setLoading(true);
    setError(null);
    setLeadsError(null);
    setOverview(null);
    setPreviousOverview(null);
    setPreviousRange(null);
    setTimeseries([]);
    setSources([]);
    setCountries([]);
    setDevices([]);
    setPages([]);
    setOrganicLeadsTotal(null);
    setPreviousOrganicLeads(null);
    setOrganicLeadsTimeseries([]);

    const params = {
      portal: selectedPortal,
      startDate: startStr,
      endDate: endStr,
    };
    const bucket = aggregationToBucket(effectiveAggregation);
    const bucketedParams = { ...params, bucket };

    // Gate the leads request on the portal's hasLeads flag — non-lead
    // portals (borncity, kme, etc.) resolve immediately to null so we never
    // hit /api/ga4/leads for them. For lead-enabled portals, failures are
    // handled below via Promise.allSettled and routed into `leadsError`
    // without taking down the rest of the dashboard.
    const leadsPromise = portalForFetch?.hasLeads
      ? api.get('/api/ga4/leads', {
          params: { ...bucketedParams, compare: 1 },
        })
      : Promise.resolve(null);

    // Critical-review finding #2: use Promise.allSettled so a leads failure
    // (or any single breakdown failure) doesn't take down the whole page.
    // The core traffic data (overview, timeseries) still renders when the
    // leads endpoint or a breakdown endpoint is degraded.
    Promise.allSettled([
      api.get('/api/ga4/overview', {
        params: { ...params, compare: 1 },
      }),
      api.get('/api/ga4/timeseries', { params: bucketedParams }),
      api.get('/api/ga4/sources', { params }),
      api.get('/api/ga4/countries', { params }),
      api.get('/api/ga4/devices', { params }),
      api.get('/api/ga4/top-pages', { params }),
      leadsPromise,
    ])
      .then((results) => {
        if (cancelled) return;
        const [ovRes, tsRes, srcsRes, ctryRes, devRes, pgRes, ldRes] = results;

        // Core metric #1: overview (treated as critical — its failure
        // surfaces as the main red error banner)
        if (ovRes.status === 'fulfilled') {
          const ovData = ovRes.value.data?.data as OverviewCompareResponse | null;
          if (ovData && 'current' in ovData) {
            setOverview(ovData.current);
            setPreviousOverview(ovData.previous);
            setPreviousRange(ovData.previousRange);
          } else if (ovData) {
            setOverview(ovData as unknown as Overview);
          }
        } else {
          const err: any = ovRes.reason;
          setError(err?.response?.data?.error || err?.message || 'Overview-Request fehlgeschlagen');
        }

        // Core metric #2: timeseries (also critical — powers the chart)
        if (tsRes.status === 'fulfilled') {
          setTimeseries((tsRes.value.data?.data as TimeseriesRow[]) || []);
        } else {
          const err: any = tsRes.reason;
          setError(
            (prev) =>
              prev ||
              err?.response?.data?.error ||
              err?.message ||
              'Timeseries-Request fehlgeschlagen',
          );
        }

        // Breakdowns (non-critical — individual failures just clear that
        // section, no global error banner)
        if (srcsRes.status === 'fulfilled') {
          setSources(srcsRes.value.data?.data || []);
        }
        if (ctryRes.status === 'fulfilled') {
          setCountries(ctryRes.value.data?.data || []);
        }
        if (devRes.status === 'fulfilled') {
          setDevices(devRes.value.data?.data || []);
        }
        if (pgRes.status === 'fulfilled') {
          setPages(pgRes.value.data?.data || []);
        }

        // Leads: dedicated error bucket, never blocks the rest of the page
        if (ldRes.status === 'fulfilled') {
          const ldValue = ldRes.value;
          if (ldValue?.data?.success && ldValue.data.data) {
            const ldData = ldValue.data.data as LeadsCompareResponse;
            setOrganicLeadsTotal(ldData.current.organicLeads);
            setPreviousOrganicLeads(ldData.previous.organicLeads);
            setOrganicLeadsTimeseries(ldData.organicTimeseries || []);
          }
          // If ldValue is null (hasLeads=false), lead state stays null/[]
        } else if (portalForFetch?.hasLeads) {
          const err: any = ldRes.reason;
          setLeadsError(
            err?.response?.data?.error || err?.message || 'Leads-Request fehlgeschlagen',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mode, selectedPortal, startStr, endStr, effectiveAggregation, portals]);

  // Group portals for the dropdown
  const groupedPortals = useMemo(() => {
    const groups: Record<string, Portal[]> = {};
    for (const group of GROUP_ORDER) groups[group] = [];
    for (const p of portals) {
      if (!groups[p.group]) groups[p.group] = [];
      groups[p.group].push(p);
    }
    for (const g of GROUP_ORDER) {
      // Primary portals first, then secondary, alphabetical within
      groups[g].sort((a, b) => {
        if (a.primary !== b.primary) return a.primary ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
    }
    return groups;
  }, [portals]);

  const currentPortal = portals.find((p) => p.key === selectedPortal);

  // Match current filter date range against quick ranges so the active button
  // highlights correctly even after the user picked one.
  const activeQuickRange = useMemo(() => {
    if (!filterState.startDate || !filterState.endDate) return null;
    const curStart = moment(filterState.startDate).format('YYYY-MM-DD');
    const curEnd = moment(filterState.endDate).format('YYYY-MM-DD');
    for (const r of QUICK_RANGES) {
      const { start, end } = r.compute();
      if (
        moment(start).format('YYYY-MM-DD') === curStart &&
        moment(end).format('YYYY-MM-DD') === curEnd
      ) {
        return r.key;
      }
    }
    return null;
  }, [filterState.startDate, filterState.endDate]);

  // Shift the date range back or forward by one period length. "back" walks
  // into the comparison period (so the user can inspect the same numbers the
  // KPI deltas were computed against); "forward" walks into the next period.
  const shiftRange = (direction: 'back' | 'forward') => {
    if (!filterState.startDate || !filterState.endDate) return;
    const start = moment(filterState.startDate);
    const end = moment(filterState.endDate);
    const days = end.diff(start, 'days') + 1;

    let newStart: moment.Moment;
    let newEnd: moment.Moment;
    if (direction === 'back') {
      newEnd = start.clone().subtract(1, 'day');
      newStart = newEnd.clone().subtract(days - 1, 'days');
    } else {
      newStart = end.clone().add(1, 'day');
      newEnd = newStart.clone().add(days - 1, 'days');
    }

    setFilterState((prev) => ({
      ...prev,
      startDate: newStart.toDate(),
      endDate: newEnd.toDate(),
    }));
  };

  // ─── Chart data ─────────────────────────────────────────────────────────
  // Backend returns rows already bucketed at the requested granularity (GA4
  // dimension yearWeek/yearMonth for traffic, COUNT(DISTINCT) GROUP BY for
  // leads). No client-side aggregation — we just plot the rows directly.
  const chartSeries: Serie[] = useMemo(() => {
    if (activeMetric === 'leads') {
      if (organicLeadsTimeseries.length === 0) return [];
      return [
        {
          id: METRIC_LABELS.leads,
          data: organicLeadsTimeseries.map((r) => ({
            x: r.date,
            y: r.leads,
          })),
        },
      ];
    }
    if (activeMetric === 'leadsPerKUsers') {
      if (organicLeadsTimeseries.length === 0 || timeseries.length === 0) return [];
      // Both inputs are bucket-aligned (same date keys), so the ratio is
      // mathematically correct per bucket.
      const usersMap = new Map(timeseries.map((r) => [r.date, r.totalUsers]));
      return [
        {
          id: METRIC_LABELS.leadsPerKUsers,
          data: organicLeadsTimeseries.map((r) => ({
            x: r.date,
            y:
              usersMap.get(r.date) && usersMap.get(r.date)! > 0
                ? Number(((r.leads / usersMap.get(r.date)!) * 1000).toFixed(2))
                : 0,
          })),
        },
      ];
    }
    if (timeseries.length === 0) return [];
    const data = timeseries.map((r) => ({
      x: r.date,
      y: (r as any)[activeMetric] || 0,
    }));
    return [
      {
        id: METRIC_LABELS[activeMetric],
        data,
      },
    ];
  }, [timeseries, organicLeadsTimeseries, activeMetric]);

  const deviceBarData = useMemo(
    () =>
      devices.map((d) => ({
        device: d.deviceCategory,
        Sitzungen: d.sessions,
        Users: d.users,
      })),
    [devices],
  );

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <Box m="20px">
      <Header title="Portal-Traffic" subtitle="GA4 Traffic-Auswertung pro Portal" />

      {/* Quick date ranges */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: colors.primary[400],
          ...paperPolish,
        }}
      >
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="subtitle2" sx={{ color: colors.grey[300], mr: 1 }}>
            Schnellauswahl:
          </Typography>
          <ButtonGroup size="small" variant="outlined">
            {QUICK_RANGES.map((r) => {
              const isActive = activeQuickRange === r.key;
              return (
                <Button
                  key={r.key}
                  onClick={() => {
                    const { start, end } = r.compute();
                    setFilterState((prev) => ({
                      ...prev,
                      startDate: start,
                      endDate: end,
                    }));
                  }}
                  sx={{
                    color: isActive ? colors.grey[100] : colors.grey[300],
                    backgroundColor: isActive ? colors.blueAccent[700] : 'transparent',
                    borderColor: colors.grey[500],
                    '&:hover': {
                      backgroundColor: colors.blueAccent[600],
                      borderColor: colors.grey[300],
                    },
                  }}
                >
                  {r.label}
                </Button>
              );
            })}
          </ButtonGroup>
        </Box>
      </Paper>

      {/* Mode toggle: Einzelansicht vs Vergleich */}
      <Paper
        sx={{
          p: 1.5,
          mb: 2,
          backgroundColor: colors.primary[400],
          ...paperPolish,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="subtitle2" sx={{ color: colors.grey[300], mr: 1 }}>
            Ansicht:
          </Typography>
          <ToggleButtonGroup
            value={mode}
            exclusive
            size="small"
            onChange={(_, v) => {
              if (!v) return;
              setMode(v);
              // When entering compare mode with an empty list, preselect
              // the current single portal so the user sees something immediately.
              if (v === 'compare' && comparePortals.length === 0) {
                setComparePortals([selectedPortal]);
              }
              // Reset metric if entering compare mode with lead metric and no
              // selected portals have leads (handled inside ComparisonView too)
            }}
          >
            <ToggleButton value="single">Einzelansicht</ToggleButton>
            <ToggleButton value="compare">Vergleich</ToggleButton>
          </ToggleButtonGroup>
          {mode === 'compare' && (
            <Typography variant="caption" sx={{ color: colors.grey[300], ml: 1 }}>
              {comparePortals.length === 0
                ? 'Keine Portale ausgewählt'
                : `${comparePortals.length} Portal${comparePortals.length === 1 ? '' : 'e'} ausgewählt`}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* boerse-express.com data-quality warning. GA4 tracking was broken for
          ~60 days before being fixed on 2026-04-15. Only data from
          2026-04-16 onward is reliable for that portal. */}
      {((mode === 'single' && selectedPortal === 'boerse_express') ||
        (mode === 'compare' && comparePortals.includes('boerse_express'))) && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: 'rgba(245, 158, 11, 0.12)',
            borderLeft: '4px solid #f59e0b',
            ...paperPolish,
          }}
        >
          <Box display="flex" alignItems="flex-start" gap={1.5}>
            <WarningAmberOutlined sx={{ color: '#f59e0b', fontSize: 22, mt: 0.25 }} />
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ color: colors.grey[100], fontWeight: 700, mb: 0.5 }}
              >
                Hinweis: GA4-Tracking für boerse-express.com war defekt
              </Typography>
              <Typography variant="body2" sx={{ color: colors.grey[200], lineHeight: 1.5 }}>
                Das GA4-Tracking für <strong>boerse-express.com</strong> war ca. 60 Tage defekt und
                wurde am <strong>15.04.2026</strong> repariert. Daten vor dem{' '}
                <strong>16.04.2026</strong> sind unvollständig — nur Werte ab dem 16.04.2026 sind
                belastbar. Andere Portale sind nicht betroffen.
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {mode === 'compare' && (
        <PortalComparisonView
          portals={portals}
          groupLabels={groupLabels}
          startStr={startStr}
          endStr={endStr}
          comparePortals={comparePortals}
          setComparePortals={setComparePortals}
          activeMetric={activeMetric}
          setActiveMetric={setActiveMetric}
          previousRange={previousRange}
          setPreviousRange={setPreviousRange}
        />
      )}

      {/* Portal selector */}
      {mode === 'single' && (
        <>
          {/* Portal selector */}
          <Paper
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: colors.primary[400],
              ...paperPolish,
            }}
          >
            <FormControl fullWidth>
              <InputLabel id="portal-select-label">Portal</InputLabel>
              <Select
                labelId="portal-select-label"
                value={selectedPortal}
                label="Portal"
                onChange={(e) => {
                  const newPortal = e.target.value as string;
                  setSelectedPortal(newPortal);
                  // Reset chart metric if switching to a non-lead portal while "leads" is selected
                  const newPortalData = portals.find((p) => p.key === newPortal);
                  if (
                    (activeMetric === 'leads' || activeMetric === 'leadsPerKUsers') &&
                    newPortalData &&
                    !newPortalData.hasLeads
                  ) {
                    setActiveMetric('totalUsers');
                  }
                }}
              >
                {GROUP_ORDER.flatMap((group) => [
                  <ListSubheader key={`sub-${group}`}>{groupLabels[group] || group}</ListSubheader>,
                  ...(groupedPortals[group] || []).map((p) => (
                    <MenuItem key={p.key} value={p.key}>
                      {p.name}
                      {!p.primary && (
                        <Chip
                          label="On-demand"
                          size="small"
                          sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                        />
                      )}
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>
            {currentPortal && !currentPortal.primary && (
              <Typography
                variant="caption"
                sx={{ mt: 1, display: 'block', color: colors.grey[300] }}
              >
                Wird live aus GA4 geladen (nicht im Snapshot-Cache).
              </Typography>
            )}
          </Paper>

          {error && (
            <Paper
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: colors.redAccent[700],
                color: colors.grey[100],
              }}
            >
              <Typography>Fehler: {error}</Typography>
            </Paper>
          )}

          {leadsError && (
            <Paper
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: 'rgba(245, 158, 11, 0.12)',
                borderLeft: '4px solid #f59e0b',
                ...paperPolish,
              }}
            >
              <Box display="flex" alignItems="flex-start" gap={1.5}>
                <WarningAmberOutlined sx={{ color: '#f59e0b', fontSize: 22, mt: 0.25 }} />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: colors.grey[100], fontWeight: 700, mb: 0.5 }}
                  >
                    Lead-Daten konnten nicht geladen werden
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.grey[200], lineHeight: 1.5 }}>
                    Traffic-Zahlen sind aktuell, aber die Lead-Abfrage ist fehlgeschlagen:{' '}
                    {leadsError}. Die Lead-KPIs werden in diesem Zeitraum ausgeblendet.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {loading && (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          )}

          {/* KPI cards */}
          {overview && (
            <>
              {previousRange && (
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
                  <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                    Vergleich zum Vorzeitraum:{' '}
                    {moment(previousRange.startDate).format('DD.MM.YYYY')} –{' '}
                    {moment(previousRange.endDate).format('DD.MM.YYYY')}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => shiftRange('back')}
                    sx={{
                      ml: 1,
                      color: colors.grey[100],
                      borderColor: colors.grey[500],
                      textTransform: 'none',
                      py: 0,
                      fontSize: '0.7rem',
                      '&:hover': {
                        backgroundColor: colors.blueAccent[700],
                        borderColor: colors.grey[300],
                      },
                    }}
                  >
                    ← Vorzeitraum anzeigen
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => shiftRange('forward')}
                    sx={{
                      color: colors.grey[100],
                      borderColor: colors.grey[500],
                      textTransform: 'none',
                      py: 0,
                      fontSize: '0.7rem',
                      '&:hover': {
                        backgroundColor: colors.blueAccent[700],
                        borderColor: colors.grey[300],
                      },
                    }}
                  >
                    Nachfolgezeitraum →
                  </Button>
                </Box>
              )}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <KpiCard
                    label="Unique Users"
                    value={fmtInt(overview.totalUsers)}
                    previousValue={
                      previousOverview ? fmtInt(previousOverview.totalUsers) : undefined
                    }
                    deltaPct={
                      previousOverview
                        ? calcDelta(overview.totalUsers, previousOverview.totalUsers)
                        : undefined
                    }
                    icon={<PeopleAltOutlined sx={{ fontSize: 22 }} />}
                    iconColor={kpiAccents.volume.iconColor}
                    iconBg={kpiAccents.volume.iconBg}
                    tooltip={METRIC_TOOLTIPS.totalUsers}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <KpiCard
                    label="Neue Users"
                    value={fmtInt(overview.newUsers)}
                    previousValue={previousOverview ? fmtInt(previousOverview.newUsers) : undefined}
                    deltaPct={
                      previousOverview
                        ? calcDelta(overview.newUsers, previousOverview.newUsers)
                        : undefined
                    }
                    icon={<PersonAddAlt1Outlined sx={{ fontSize: 22 }} />}
                    iconColor={kpiAccents.volume.iconColor}
                    iconBg={kpiAccents.volume.iconBg}
                    tooltip={METRIC_TOOLTIPS.newUsers}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <KpiCard
                    label="Sitzungen"
                    value={fmtInt(overview.sessions)}
                    previousValue={previousOverview ? fmtInt(previousOverview.sessions) : undefined}
                    deltaPct={
                      previousOverview
                        ? calcDelta(overview.sessions, previousOverview.sessions)
                        : undefined
                    }
                    icon={<LanguageOutlined sx={{ fontSize: 22 }} />}
                    iconColor={kpiAccents.volume.iconColor}
                    iconBg={kpiAccents.volume.iconBg}
                    tooltip={METRIC_TOOLTIPS.sessions}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <KpiCard
                    label="Seitenaufrufe"
                    value={fmtInt(overview.pageViews)}
                    previousValue={
                      previousOverview ? fmtInt(previousOverview.pageViews) : undefined
                    }
                    deltaPct={
                      previousOverview
                        ? calcDelta(overview.pageViews, previousOverview.pageViews)
                        : undefined
                    }
                    icon={<VisibilityOutlined sx={{ fontSize: 22 }} />}
                    iconColor={kpiAccents.volume.iconColor}
                    iconBg={kpiAccents.volume.iconBg}
                    tooltip={METRIC_TOOLTIPS.pageViews}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <KpiCard
                    label="Ø Sitzungsdauer"
                    value={fmtDuration(overview.avgSessionDurationSeconds)}
                    previousValue={
                      previousOverview
                        ? fmtDuration(previousOverview.avgSessionDurationSeconds)
                        : undefined
                    }
                    deltaPct={
                      previousOverview
                        ? calcDelta(
                            overview.avgSessionDurationSeconds,
                            previousOverview.avgSessionDurationSeconds,
                          )
                        : undefined
                    }
                    sub="mm:ss"
                    icon={<AccessTimeOutlined sx={{ fontSize: 22 }} />}
                    iconColor={kpiAccents.quality.iconColor}
                    iconBg={kpiAccents.quality.iconBg}
                    tooltip={METRIC_TOOLTIPS.avgSessionDurationSeconds}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <KpiCard
                    label="Engagement-Rate"
                    value={fmtPercent(overview.engagementRate)}
                    previousValue={
                      previousOverview ? fmtPercent(previousOverview.engagementRate) : undefined
                    }
                    deltaPct={
                      previousOverview
                        ? calcDelta(overview.engagementRate, previousOverview.engagementRate)
                        : undefined
                    }
                    icon={<ThumbUpOffAltOutlined sx={{ fontSize: 22 }} />}
                    iconColor={kpiAccents.quality.iconColor}
                    iconBg={kpiAccents.quality.iconBg}
                    tooltip={METRIC_TOOLTIPS.engagementRate}
                  />
                </Grid>
                {organicLeadsTotal !== null && (
                  <>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                      <KpiCard
                        label="Gesamt-Leads (Traffic)"
                        value={fmtInt(organicLeadsTotal)}
                        previousValue={
                          previousOrganicLeads !== null ? fmtInt(previousOrganicLeads) : undefined
                        }
                        deltaPct={
                          previousOrganicLeads !== null
                            ? calcDelta(organicLeadsTotal, previousOrganicLeads)
                            : undefined
                        }
                        icon={<HowToRegOutlined sx={{ fontSize: 22 }} />}
                        iconColor={kpiAccents.conversion.iconColor}
                        iconBg={kpiAccents.conversion.iconBg}
                        tooltip={METRIC_TOOLTIPS.leads}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                      <KpiCard
                        label="Leads / 1.000 Users"
                        value={
                          overview.totalUsers > 0
                            ? ((organicLeadsTotal / overview.totalUsers) * 1000).toFixed(1)
                            : '0'
                        }
                        previousValue={
                          previousOrganicLeads !== null &&
                          previousOverview &&
                          previousOverview.totalUsers > 0
                            ? ((previousOrganicLeads / previousOverview.totalUsers) * 1000).toFixed(
                                1,
                              )
                            : undefined
                        }
                        deltaPct={
                          previousOrganicLeads !== null &&
                          previousOverview &&
                          previousOverview.totalUsers > 0 &&
                          overview.totalUsers > 0
                            ? calcDelta(
                                organicLeadsTotal / overview.totalUsers,
                                previousOrganicLeads / previousOverview.totalUsers,
                              )
                            : undefined
                        }
                        icon={<InsightsOutlined sx={{ fontSize: 22 }} />}
                        iconColor={kpiAccents.conversion.iconColor}
                        iconBg={kpiAccents.conversion.iconBg}
                        tooltip={METRIC_TOOLTIPS.leadsPerKUsers}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </>
          )}

          {/* Time-series chart */}
          <Paper
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: colors.primary[400],
              ...paperPolish,
            }}
          >
            <Typography variant="h5" color={colors.grey[100]} mb={1}>
              Verlauf
            </Typography>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <ToggleButtonGroup
                  value={aggregationMode}
                  exclusive
                  size="small"
                  onChange={(_, v) => v && setAggregationMode(v)}
                >
                  <ToggleButton value="auto">
                    Auto
                    {aggregationMode === 'auto' && (
                      <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.7 }}>
                        (
                        {effectiveAggregation === 'daily'
                          ? 'tägl.'
                          : effectiveAggregation === 'weekly'
                            ? 'wö.'
                            : 'mo.'}
                        )
                      </Typography>
                    )}
                  </ToggleButton>
                  <ToggleButton value="daily">Täglich</ToggleButton>
                  <ToggleButton value="weekly">Wöchentlich</ToggleButton>
                  <ToggleButton value="monthly">Monatlich</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup
                  value={activeMetric}
                  exclusive
                  size="small"
                  onChange={(_, v) => v && setActiveMetric(v)}
                >
                  {(Object.keys(METRIC_LABELS) as MetricKey[])
                    .filter((k) => k !== 'avgSessionDurationSeconds')
                    .filter(
                      (k) =>
                        (k !== 'leads' && k !== 'leadsPerKUsers') || organicLeadsTotal !== null,
                    )
                    .map((k) => (
                      <ToggleButton key={k} value={k}>
                        {METRIC_LABELS[k]}
                      </ToggleButton>
                    ))}
                </ToggleButtonGroup>
              </Box>
            </Box>
            <Box height={400}>
              {chartSeries.length > 0 && chartSeries[0].data.length > 0 && (
                <ResponsiveLine
                  data={chartSeries}
                  theme={{
                    axis: {
                      domain: { line: { stroke: colors.grey[100] } },
                      legend: { text: { fill: colors.grey[100] } },
                      ticks: {
                        line: { stroke: colors.grey[100], strokeWidth: 1 },
                        text: { fill: colors.grey[100] },
                      },
                    },
                    legends: { text: { fill: colors.grey[100] } },
                    tooltip: { container: { color: colors.primary[500] } },
                  }}
                  colors={[colors.greenAccent[500]]}
                  margin={{ top: 20, right: 30, bottom: 60, left: 70 }}
                  xScale={{ type: 'point' }}
                  yScale={{
                    type: 'linear',
                    min: 0,
                    max: 'auto',
                    stacked: false,
                    reverse: false,
                  }}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: -45,
                    legend: 'Datum',
                    legendOffset: 50,
                    legendPosition: 'middle',
                    format: (v) => formatAggregationTick(String(v), effectiveAggregation),
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: METRIC_LABELS[activeMetric],
                    legendOffset: -60,
                    legendPosition: 'middle',
                    format: (v) =>
                      activeMetric === 'engagementRate'
                        ? `${(Number(v) * 100).toFixed(0)}%`
                        : Number(v).toLocaleString('de-DE'),
                  }}
                  pointSize={6}
                  pointColor={{ theme: 'background' }}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  useMesh={true}
                  enableArea={true}
                  areaOpacity={0.1}
                  tooltip={({ point }) => (
                    <div
                      style={{
                        background: colors.primary[400],
                        padding: '8px 12px',
                        border: `1px solid ${colors.grey[100]}`,
                        borderRadius: 4,
                        color: colors.grey[100],
                      }}
                    >
                      <strong>{point.data.xFormatted}</strong>
                      <br />
                      {METRIC_LABELS[activeMetric]}:{' '}
                      {activeMetric === 'engagementRate'
                        ? fmtPercent(Number(point.data.y))
                        : fmtInt(Number(point.data.y))}
                    </div>
                  )}
                />
              )}
            </Box>
          </Paper>

          {/* Breakdown row 1: Sources + Top Pages */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: colors.primary[400],
                  height: '100%',
                  ...paperPolish,
                }}
              >
                <Typography variant="h5" color={colors.grey[100]} mb={2}>
                  Top Traffic-Quellen
                </Typography>
                <TableContainer sx={{ backgroundColor: '#ffffff', maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                          }}
                        >
                          Quelle / Medium
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                          }}
                        >
                          Sitzungen
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                          }}
                        >
                          Users
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sources.map((row, idx) => (
                        <TableRow key={`${row.source}-${row.medium}-${idx}`}>
                          <TableCell sx={{ color: 'black' }}>
                            {row.source} / {row.medium}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'black' }}>
                            {fmtInt(row.sessions)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'black' }}>
                            {fmtInt(row.users)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {sources.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ color: 'black' }}>
                            Keine Daten
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: colors.primary[400],
                  height: '100%',
                  ...paperPolish,
                }}
              >
                <Typography variant="h5" color={colors.grey[100]} mb={2}>
                  Top Seiten
                </Typography>
                <TableContainer sx={{ backgroundColor: '#ffffff', maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                          }}
                        >
                          Pfad
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                          }}
                        >
                          Aufrufe
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                          }}
                        >
                          Users
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pages.map((row, idx) => (
                        <TableRow key={`${row.pagePath}-${idx}`}>
                          <TableCell
                            sx={{
                              color: 'black',
                              maxWidth: 320,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={row.pagePath}
                          >
                            {row.pagePath}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'black' }}>
                            {fmtInt(row.pageViews)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'black' }}>
                            {fmtInt(row.users)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {pages.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ color: 'black' }}>
                            Keine Daten
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Breakdown row 2: Countries + Devices */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: colors.primary[400],
                  height: '100%',
                  ...paperPolish,
                }}
              >
                <Typography variant="h5" color={colors.grey[100]} mb={2}>
                  Top Länder
                </Typography>
                <TableContainer sx={{ backgroundColor: '#ffffff', maxHeight: 360 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                          }}
                        >
                          Land
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                          }}
                        >
                          Sitzungen
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                          }}
                        >
                          Users
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {countries.map((row, idx) => (
                        <TableRow key={`${row.country}-${idx}`}>
                          <TableCell sx={{ color: 'black' }}>{row.country}</TableCell>
                          <TableCell align="right" sx={{ color: 'black' }}>
                            {fmtInt(row.sessions)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'black' }}>
                            {fmtInt(row.users)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {countries.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ color: 'black' }}>
                            Keine Daten
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: colors.primary[400],
                  height: '100%',
                  ...paperPolish,
                }}
              >
                <Typography variant="h5" color={colors.grey[100]} mb={2}>
                  Geräte
                </Typography>
                <Box height={360}>
                  {deviceBarData.length > 0 && (
                    <ResponsiveBar
                      data={deviceBarData as any}
                      keys={['Sitzungen', 'Users']}
                      indexBy="device"
                      groupMode="grouped"
                      margin={{ top: 20, right: 30, bottom: 50, left: 70 }}
                      padding={0.3}
                      colors={[colors.blueAccent[500], colors.greenAccent[500]]}
                      theme={{
                        axis: {
                          domain: { line: { stroke: colors.grey[100] } },
                          legend: { text: { fill: colors.grey[100] } },
                          ticks: {
                            line: { stroke: colors.grey[100], strokeWidth: 1 },
                            text: { fill: colors.grey[100] },
                          },
                        },
                        legends: { text: { fill: colors.grey[100] } },
                        tooltip: { container: { color: colors.primary[500] } },
                      }}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        format: (v) => Number(v).toLocaleString('de-DE'),
                      }}
                      labelSkipWidth={12}
                      labelSkipHeight={12}
                      legends={[
                        {
                          dataFrom: 'keys',
                          anchor: 'top-right',
                          direction: 'row',
                          translateX: 0,
                          translateY: -10,
                          itemsSpacing: 5,
                          itemWidth: 90,
                          itemHeight: 20,
                          symbolSize: 12,
                          symbolShape: 'square',
                        },
                      ]}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default Ga4TrafficPage;
